import React, { useRef, useState } from "react";

import { UploadCloud } from "lucide-react";

interface Props {
  /** Comma-separated extensions, e.g. ".pdf,.pptx". */
  accept: string;
  maxBytes: number;
  onFileSelected: (file: File) => void;
  disabled?: boolean;
  hint?: string;
}

const formatMb = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

const DocumentDropzone: React.FC<Props> = ({
  accept,
  maxBytes,
  onFileSelected,
  disabled = false,
  hint,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowed = accept
    .split(",")
    .map(ext => ext.trim().toLowerCase())
    .filter(Boolean);

  const handleFile = (file?: File | null) => {
    if (!file || disabled) return;
    const name = file.name.toLowerCase();
    if (!allowed.some(ext => name.endsWith(ext))) {
      // An old PowerPoint file is the common miss: say how to fix it, in the
      // server's words (design P2, F-UF-E05).
      setError(
        name.endsWith(".ppt") && allowed.includes(".pptx")
          ? "Old .ppt files aren't supported. Open it in PowerPoint and save it as .pptx."
          : `Unsupported file type. Use ${allowed.join(", ")}.`
      );
      return;
    }
    if (file.size > maxBytes) {
      setError(`File is too large. The limit is ${formatMb(maxBytes)}.`);
      return;
    }
    setError(null);
    onFileSelected(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={handleKeyDown}
        onDragOver={e => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
          dragging
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
        } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      >
        <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
          <UploadCloud className="h-6 w-6 text-blue-600" aria-hidden />
        </span>
        <p className="text-sm font-semibold text-gray-900">
          Drop a document here, or{" "}
          <span className="text-blue-600 underline">browse</span>
        </p>
        {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={e => {
            handleFile(e.target.files?.[0]);
            // Allow re-selecting the same file after an error or reset.
            e.target.value = "";
          }}
        />
      </div>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default DocumentDropzone;
